import React from 'react';
import {connect} from 'react-redux';

import {assert} from 'Asserts';
import * as DataTableConstants from 'notebook/data-tables/DataTableConstants';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import {RegistrationTable} from 'notebook/registration-tables/RegistrationTableConstants';
import AddStageRunSamplesModal from 'notebook/sample-propagation/legacy/legacy-add-samples-modal/AddStageRunSamplesModal';
import {Table} from 'notebook/tables/TableConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import useTagSchemaIdsQuery from 'schemas/useTagSchemaIdsQuery';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/LegacyDropdownMenuConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import ModalAnchor from 'ui-library/ModalAnchor';
import GetStageRunsWrapper from 'workflows/stage-entries/components/GetStageRunsWrapper';
import * as StageRunActions from 'workflows/StageRunActions';
import {
  getAvailableSampleKeysByTableName,
  getTableIconsByTableName,
  resolveSampleKeysByTableName,
  SampleDetailsByTableName,
  SampleDetailsWithStageRunSample,
} from 'workflows/util/StageRunUtil';
import {RegistrationRecordList, StageRun, StageRunSample} from 'workflows/WorkflowConstants';

interface EnhancedProps {
  registrationRecordListsById: {[registrationRecordListId: string]: RegistrationRecordList};
}
function mapStateToProps(state: BenchlingState): EnhancedProps {
  return {
    registrationRecordListsById: state.models.registrationRecordListsById,
  };
}

interface BaseDataTableStageRunSampleLinkContextProps {
  children: (args: {
    table: Table;
    sampleIds: Array<string>;
    addMenuItems: Array<LegacyDropdownMenuItem>;
  }) => JSX.Element;
  dataTable: DataTableConstants.DataTable;
  entryId: string;
  onUpdate: (dataTable: DataTableConstants.DataTable) => void;
  registrationTables: Array<RegistrationTable>;
}

export interface DataTableStageRunSampleLinkContextProps
  extends BaseDataTableStageRunSampleLinkContextProps, EnhancedProps {
  tagSchemaIds: Set<string>;
}

interface State {
  open: boolean;
}

/**
 * DataTableStageRunSampleLinkContext is a wrapper to DataTableEditable to provide data fetching, new row
 * insertion, and conversion to the TableEditable-compatible form, where each row is linked to a stage run
 * and sample.
 */
class DataTableStageRunSampleLinkContext extends React.Component<
  DataTableStageRunSampleLinkContextProps,
  State
> {
  override state: State = {
    open: false,
  };

  override componentDidMount(): void {
    StageRunActions.fetchStageRunSamples(
      this.props.dataTable.rows.map((row) => row.stageRunSampleId!),
      {
        includeBatches: false,
      }
    );
  }

  _handleAddClick = (): void => {
    this.setState({open: true});
  };

  _handleCloseModal = (): void => {
    this.setState({open: false});
  };

  _getAvailableStageRunSampleDetailsByTableName(
    stageRuns: Array<StageRun>,
    stageRunSampleById: {[stageRunSampleId: string]: StageRunSample}
  ): SampleDetailsByTableName {
    const {linkType} = this.props.dataTable.config;
    const availableSampleKeys = getAvailableSampleKeysByTableName({
      stageRuns,
      registrationTables: this.props.registrationTables,
      registrationRecordListsById: this.props.registrationRecordListsById,
      tagSchemaIds: this.props.tagSchemaIds,
    });

    const sampleDetails = resolveSampleKeysByTableName(availableSampleKeys, stageRuns, stageRunSampleById);
    if (linkType === FIELD_TYPES.BATCH_LINK.type) {
      for (const tableName of Object.keys(sampleDetails)) {
        sampleDetails[tableName] = sampleDetails[tableName].filter(
          ({stageRunSample}) => stageRunSample.batch
        );
      }
    } else if (linkType === FIELD_TYPES.BIOENTITY_LINK.type) {
      for (const tableName of Object.keys(sampleDetails)) {
        sampleDetails[tableName] = sampleDetails[tableName].filter(
          ({stageRunSample}) => stageRunSample.fileId
        );
      }
    } else {
      assert(false, 'Link type on data-table configuration is incorrect');
    }
    return sampleDetails;
  }

  _handleAddSamples = (sampleDetails: Array<SampleDetailsWithStageRunSample>): void => {
    let dataTable = this.props.dataTable;
    for (const {stageRun, stageRunSample} of sampleDetails) {
      dataTable = DataTableUtil.addRowToStageRunSampleTable(dataTable, stageRun.id, stageRunSample.id);
    }
    this.props.onUpdate(dataTable);
  };

  _getTableIcons(): {[tableName: string]: BenchlingIconName} {
    return getTableIconsByTableName({registrationTables: this.props.registrationTables});
  }

  override render(): JSX.Element {
    return (
      <GetStageRunsWrapper entryId={this.props.entryId}>
        {(stageRuns: Array<StageRun>, stageRunSampleById: {[stageRunSampleId: string]: StageRunSample}) => {
          const stageRunSampleDetailsByTableName = this._getAvailableStageRunSampleDetailsByTableName(
            stageRuns,
            stageRunSampleById
          );
          const rowLabels = DataTableUtil.getRowLabelsForStageEntryDataTable(
            this.props.dataTable,
            stageRuns,
            stageRunSampleById
          );
          const sampleIds = DataTableUtil.getSampleIdsForStageEntryDataTable(
            this.props.dataTable,
            stageRunSampleById
          );
          const table = DataTableUtil.convertDataTableToTable(this.props.dataTable, rowLabels);
          return (
            <div>
              <ModalAnchor open={this.state.open}>
                <AddStageRunSamplesModal
                  enableRowCounts={true}
                  title={`Add samples to ${MODEL_NAMES.result_table}`}
                  dropdownIconByTableName={this._getTableIcons()}
                  stageRunSampleDetailsByTableName={stageRunSampleDetailsByTableName}
                  onStageRunSamplesSelected={this._handleAddSamples}
                  onRequestClose={this._handleCloseModal}
                />
              </ModalAnchor>
              {this.props.children({
                table,
                sampleIds,
                addMenuItems: [
                  {
                    name: 'Add samples',
                    icon: 'compose',
                    handleOnClick: this._handleAddClick,
                  },
                ],
              })}
            </div>
          );
        }}
      </GetStageRunsWrapper>
    );
  }
}

function QueryingDataTableStageRunSampleLinkContext(
  props: BaseDataTableStageRunSampleLinkContextProps & EnhancedProps
): JSX.Element {
  const tagSchemaIds = useTagSchemaIdsQuery();
  return <DataTableStageRunSampleLinkContext {...props} tagSchemaIds={tagSchemaIds ?? new Set()} />;
}

export default connect(mapStateToProps)(QueryingDataTableStageRunSampleLinkContext);
